/**
 * Route query + session helpers for the superAdmin org-group picker.
 * Single selection; empty means no org-group filter (kaikki within universe).
 */
class OrgGroupScopeUtility {
  /**
   * @param {import('vue-router').LocationQuery} query
   * @returns {string|null}
   */
  parseId(query) {
    const raw = query?.orgGroup
    if (raw == null || raw === '') return null
    return String(Array.isArray(raw) ? raw[0] : raw)
  }

  /**
   * @param {Record<string, unknown>} query
   * @param {string|null|undefined} groupId
   * @returns {Record<string, unknown>}
   */
  withQuery(query, groupId) {
    const next = { ...query }
    if (!groupId) delete next.orgGroup
    else next.orgGroup = String(groupId)
    return next
  }

  /**
   * Intersect org-group member ids with the current scope universe.
   *
   * @param {string[]} memberAccountIds - Fresh ids from `GET /group/:id/account-ids`
   * @param {string[]} scopeAccountIds
   * @returns {string[]}
   */
  accountIdsInScope(memberAccountIds = [], scopeAccountIds = []) {
    const scopeSet = new Set((scopeAccountIds ?? []).map(String))
    return (memberAccountIds ?? []).map(String).filter((id) => id && scopeSet.has(id))
  }

  /**
   * @param {string[]} a
   * @param {string[]} b
   * @returns {boolean}
   */
  sameAccountIds(a, b) {
    const left = [...new Set((a ?? []).map(String))].sort()
    const right = [...new Set((b ?? []).map(String))].sort()
    return left.length === right.length && left.every((id, i) => id === right[i])
  }

  /**
   * True when account multiselect overrides org-group route mode.
   *
   * @param {string[]} pickedAccountIds
   * @param {string|null|undefined} orgGroupId
   * @param {string[]} memberAccountIds
   * @param {string[]} scopeAccountIds
   * @returns {boolean}
   */
  accountsBreakOrgGroupSelection(pickedAccountIds, orgGroupId, memberAccountIds = [], scopeAccountIds = []) {
    if (!orgGroupId) return false
    if (!pickedAccountIds?.length) return true
    const allowed = this.accountIdsInScope(memberAccountIds, scopeAccountIds)
    return !this.sameAccountIds(pickedAccountIds, allowed)
  }

  /**
   * @param {string|null|undefined} parsedId
   * @param {Array<{ _id: string }>} [orgGroups]
   * @returns {string|null}
   */
  sanitizeId(parsedId, orgGroups = []) {
    if (!parsedId) return null
    const known = new Set((orgGroups ?? []).map((group) => String(group._id)))
    return known.has(String(parsedId)) ? String(parsedId) : null
  }
}

export default new OrgGroupScopeUtility()
